/**
 * Generic signed webhooks (wave 2) — UI parts used by
 * routes/_authgated/app.$tenantSlug.settings.webhooks.tsx.
 *
 *   - EventTypePicker: "All events" vs an explicit subscription, grouped
 *     from GET /outbound-webhooks/event-types, plus a free-form type (the
 *     server accepts any well-formed type so a newly emitted event can be
 *     subscribed before the catalog lists it). POSH notices are flagged:
 *     "All events" never delivers them — only ticking them does. The same
 *     server rule holds back personal-finance notices (loan_*, tax_*): only
 *     a subscription that names the type receives them.
 *   - SigningSecretDialog: the ONE-TIME reveal of a whsec_… secret (after
 *     create and after rotate). Masked by default, copy button.
 *   - RotateSecretButton: AlertDialog-confirmed rotation with an overlap
 *     choice (old secret keeps signing for 24 h, or is revoked now).
 *   - DeadLetterPanel: deliveries that failed for good and are kept
 *     (retry queue full — audit F38 — and, since migration 132, retries
 *     exhausted, rejected by the endpoint, or waiting while the webhook was
 *     off) — requeue them once the endpoint is healthy, or discard them
 *     (AlertDialog). One delivery is resent from the delivery log
 *     ("Redeliver"). Renders nothing while none are kept.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  Eye,
  EyeOff,
  Inbox,
  KeyRound,
  Loader2,
  Plus,
  RotateCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { toApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  deadLetterReasonLabel,
  normaliseEventType,
} from "@/lib/webhook-format";
import {
  discardWebhookDeadLetters,
  listWebhookDeadLetters,
  listWebhookEventTypes,
  requeueWebhookDeadLetters,
  rotateWebhookSecret,
  type OutboundWebhook,
  type WebhookEventType,
} from "@/lib/api/webhooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const MAX_EVENT_TYPES = 50;

export function useWebhookEventTypes(enabled: boolean) {
  return useQuery({
    queryKey: ["webhooks", "event-types"],
    queryFn: ({ signal }) => listWebhookEventTypes(signal),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/** "All events" / "3 events" summary for a generic row. */
export function genericSubscriptionLabel(w: OutboundWebhook): string {
  if (w.event_types.length === 0)
    return "All Events (Except POSH and Personal-Finance Notices)";
  if (w.event_types.length <= 2) return w.event_types.join(", ");
  return `${w.event_types.length} events`;
}

// ---------------------------------------------------------------------------
// EventTypePicker
// ---------------------------------------------------------------------------

export function EventTypePicker({
  allEvents,
  onAllEventsChange,
  value,
  onChange,
}: {
  readonly allEvents: boolean;
  readonly onAllEventsChange: (v: boolean) => void;
  readonly value: string[];
  readonly onChange: (v: string[]) => void;
}) {
  const q = useWebhookEventTypes(true);
  const [custom, setCustom] = useState("");
  const [customError, setCustomError] = useState<string | null>(null);

  const catalog = q.data?.items ?? [];
  const known = new Set(catalog.map((e) => e.type));
  const extras = value.filter((v) => !known.has(v));
  const groups = new Map<string, WebhookEventType[]>();
  for (const e of catalog) {
    groups.set(e.group, [...(groups.get(e.group) ?? []), e]);
  }

  function toggle(t: string) {
    onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  }

  function addCustom() {
    if (!custom.trim()) return;
    const t = normaliseEventType(custom);
    if (t === null) {
      setCustomError(
        "Use lowercase letters, digits, underscores and dots (max 40), optionally ending in .* — e.g. invoice.paid or invoice.*",
      );
      return;
    }
    if (!value.includes(t)) {
      if (value.length >= MAX_EVENT_TYPES) {
        setCustomError(`At most ${MAX_EVENT_TYPES} event types per webhook`);
        return;
      }
      onChange([...value, t]);
    }
    setCustom("");
    setCustomError(null);
  }

  return (
    <div className="space-y-3">
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
          checked={allEvents}
          onChange={(e) => onAllEventsChange(e.target.checked)}
          data-testid="webhook-all-events-checkbox"
        />
        <span>
          <span className="font-medium">All events</span>
          <span className="block text-xs text-muted-foreground">
            Every event and notification type, except confidential POSH
            notices and personal-finance notices (employee loan and
            tax-declaration requests and decisions) — those are sent only
            when you tick or add them by name below. An &quot;All …
            events&quot; entry (e.g. invoice.*) covers a whole family, but
            never a confidential type.
          </span>
        </span>
      </label>

      {!allEvents && (
        <div
          className="max-h-64 space-y-3 overflow-y-auto rounded-md border p-3"
          data-testid="webhook-event-type-list"
        >
          {q.isLoading && (
            <div className="space-y-2">
              {["e1", "e2", "e3", "e4"].map((k) => (
                <Skeleton key={k} className="h-5 w-full" />
              ))}
            </div>
          )}
          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load event types</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message} You can still add a type by
                name below.
              </AlertDescription>
            </Alert>
          )}
          {[...groups.entries()].map(([group, items]) => {
            const confidential = items.some((i) => i.confidential);
            return (
              <fieldset key={group} className="space-y-1.5">
                <legend className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {confidential && (
                    <ShieldAlert aria-hidden className="h-3.5 w-3.5" />
                  )}
                  {group}
                </legend>
                {confidential && (
                  <p className="text-xs text-muted-foreground">
                    POSH notices carry no names or complaint details, but
                    their timing is confidential. Send them only to a system
                    restricted to the ICC.
                  </p>
                )}
                {items.map((e) => (
                  <label
                    key={e.type}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input accent-primary"
                      checked={value.includes(e.type)}
                      onChange={() => toggle(e.type)}
                      data-testid={`webhook-event-${e.type}`}
                    />
                    <span>{e.label}</span>
                    <code className="text-xs text-muted-foreground">
                      {e.type}
                    </code>
                  </label>
                ))}
              </fieldset>
            );
          })}
          {extras.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {extras.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 font-mono text-xs"
                >
                  {t}
                  <button
                    type="button"
                    aria-label={`Remove ${t}`}
                    onClick={() => onChange(value.filter((x) => x !== t))}
                  >
                    <X aria-hidden className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <div className="flex gap-2">
              <Input
                value={custom}
                onChange={(e) => {
                  setCustom(e.target.value);
                  setCustomError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustom();
                  }
                }}
                placeholder="Other event type, e.g. invoice.paid or invoice.*"
                aria-label="Other event type"
                className="h-8 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustom}
                className="gap-1"
              >
                <Plus aria-hidden className="h-3.5 w-3.5" />
                Add
              </Button>
            </div>
            {customError && (
              <p className="text-xs text-destructive">{customError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SigningSecretDialog — one-time reveal
// ---------------------------------------------------------------------------

export function SigningSecretDialog({
  secret,
  title,
  note,
  onClose,
}: {
  readonly secret: string;
  readonly title: string;
  readonly note?: string;
  readonly onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);

  function copy() {
    void navigator.clipboard.writeText(secret);
    toast.success("Signing secret copied");
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound aria-hidden className="h-4 w-4 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Copy the signing secret now. It will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 rounded-md border bg-muted px-3 py-2">
          <div className="flex items-center gap-2">
            <code
              className="flex-1 break-all font-mono text-xs"
              data-testid="webhook-signing-secret"
            >
              {visible ? secret : secret.replace(/./g, "•")}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide secret" : "Show secret"}
            >
              {visible ? (
                <EyeOff aria-hidden className="h-4 w-4" />
              ) : (
                <Eye aria-hidden className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={copy}
              aria-label="Copy secret"
            >
              <Copy aria-hidden className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-1.5 text-xs text-muted-foreground">
          <p>
            Your endpoint should verify every delivery: compute
            HMAC-SHA256 with this secret over{" "}
            <code>&lt;t&gt;.&lt;raw body&gt;</code> (t from the{" "}
            <code>X-WorkRight-Signature</code> header) and compare it with
            the <code>v1</code> value, reject timestamps older than 5
            minutes, and ignore a repeated{" "}
            <code>X-WorkRight-Delivery</code> id.
          </p>
          {note && <p>{note}</p>}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done — I have stored the secret</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RotateSecretButton
// ---------------------------------------------------------------------------

export function RotateSecretButton({
  webhook,
  disabled,
}: {
  readonly webhook: OutboundWebhook;
  readonly disabled?: boolean;
}) {
  const qc = useQueryClient();
  const [revokeNow, setRevokeNow] = useState(false);
  const [revealed, setRevealed] = useState<{
    secret: string;
    until?: string | null;
  } | null>(null);
  const rotate = useMutation({
    mutationFn: (overlapHours: number) =>
      rotateWebhookSecret(webhook.id, overlapHours),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["webhooks", "list"] });
      setRevealed({
        secret: res.signing_secret,
        until: res.previous_valid_until,
      });
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  return (
    <>
      <AlertDialog onOpenChange={(o) => o && setRevokeNow(false)}>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={disabled || rotate.isPending}
            data-testid={`webhook-rotate-btn-${webhook.id}`}
          >
            {rotate.isPending ? (
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCw aria-hidden className="h-3.5 w-3.5" />
            )}
            Rotate Secret
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the signing secret?</AlertDialogTitle>
            <AlertDialogDescription>
              A new secret for &quot;{webhook.label}&quot; is generated and
              shown once.{" "}
              {revokeNow
                ? "The current secret stops working immediately — deliveries fail verification until your endpoint uses the new one."
                : "For 24 hours every delivery is signed with both secrets, so you can update your endpoint without missing events."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              checked={revokeNow}
              onChange={(e) => setRevokeNow(e.target.checked)}
            />
            <span>
              Revoke the current secret now (use this if it has leaked)
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rotate.mutate(revokeNow ? 0 : 24)}
              className={
                revokeNow
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined
              }
            >
              Rotate Secret
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {revealed && (
        <SigningSecretDialog
          secret={revealed.secret}
          title={`New Signing Secret: ${webhook.label}`}
          note={
            revealed.until
              ? `The previous secret keeps signing until ${formatDateTime(revealed.until)}.`
              : "The previous secret no longer signs deliveries."
          }
          onClose={() => setRevealed(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DeadLetterPanel — parked deliveries (retry queue was full)
// ---------------------------------------------------------------------------

export function DeadLetterPanel({
  webhook,
}: Readonly<{ webhook: OutboundWebhook }>) {
  const qc = useQueryClient();
  const key = ["webhooks", "dead-letters", webhook.id] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => listWebhookDeadLetters(webhook.id, signal),
    staleTime: 15 * 1000,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["webhooks", "deliveries", webhook.id] });
  };
  const requeue = useMutation({
    mutationFn: () => requeueWebhookDeadLetters(webhook.id),
    onSuccess: (r) => {
      refresh();
      if (r.requeued === 0) {
        toast.error(
          "Nothing was requeued — the retry queue is still full. Try again once the queued retries have gone through.",
        );
      } else {
        toast.success(
          `${r.requeued} parked deliver${r.requeued === 1 ? "y" : "ies"} requeued` +
            (r.remaining > 0 ? `; ${r.remaining} still parked (queue full).` : "."),
        );
      }
    },
    onError: (err) => toast.error(toApiError(err).message),
  });
  const discard = useMutation({
    mutationFn: () => discardWebhookDeadLetters(webhook.id),
    onSuccess: (r) => {
      refresh();
      toast.success(`${r.discarded} parked deliver${r.discarded === 1 ? "y" : "ies"} discarded.`);
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  if (q.isLoading) return <Skeleton className="mx-4 mb-4 h-16" />;
  if (q.isError) {
    return (
      <p className="px-4 pb-4 text-sm text-destructive">{toApiError(q.error).message}</p>
    );
  }
  const st = q.data;
  if (!st || st.dead_letters === 0) return null;
  const busy = requeue.isPending || discard.isPending;

  return (
    <div className="px-4 pb-4" data-testid={`webhook-dead-letters-${webhook.id}`}>
      <Alert className="border-amber-300 bg-amber-50/40">
        <Inbox aria-hidden className="h-4 w-4 text-amber-700" />
        <AlertTitle className="text-amber-900">
          {st.dead_letters} failed deliver{st.dead_letters === 1 ? "y" : "ies"} kept — not retried
        </AlertTitle>
        <AlertDescription className="space-y-3 text-amber-900">
          <p>
            These deliveries failed for good — every retry failed, the
            endpoint rejected them, the webhook was off, or the retry queue
            was full ({st.queue_cap} waiting retries) — so they are kept
            instead of lost. Fix your endpoint, then requeue them all here
            or resend one from the delivery log; or discard them. Kept
            deliveries are dropped after {st.retention_days} days.
            {st.queued > 0 && ` ${st.queued} retr${st.queued === 1 ? "y is" : "ies are"} still queued.`}
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
            {st.items.map((d) => (
              <li key={d.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium">{d.event_type}</span>
                <span className="font-mono text-[11px] text-amber-900/80">{d.delivery_id}</span>
                <span className="text-amber-900/80">
                  {deadLetterReasonLabel(d.reason)} ·{" "}
                  {formatDateTime(d.dead_lettered_at)}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy || !webhook.is_active}
              title={webhook.is_active ? undefined : "Turn the webhook on to requeue"}
              onClick={() => requeue.mutate()}
              data-testid={`webhook-dead-letters-requeue-${webhook.id}`}
            >
              {requeue.isPending ? (
                <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCw aria-hidden className="h-3.5 w-3.5" />
              )}
              Requeue
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  disabled={busy}
                >
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                  Discard
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Discard {st.dead_letters} parked deliveries?</AlertDialogTitle>
                  <AlertDialogDescription>
                    They will never be sent. The delivery log keeps the record
                    of the failed first attempts. This can&apos;t be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => discard.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Discard
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

